import { useRef, useState } from 'react';
import { Avatar, Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useUploadAvatarMutation } from '@shared/queries';

type Props = {
  userId?: string;
  currentUrl?: string | null;
  name?: string;
};

const compressImage = (imageData: string, maxWidth = 512, quality = 0.8): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = imageData;
  });

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AvatarUploadButton = ({ userId, currentUrl, name }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploadAvatarMutation = useUploadAvatarMutation();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setError('Vyberte obrázek (JPEG, PNG, WEBP).');
      return;
    }

    if (!userId) {
      setError('Chybí ID uživatele pro nahrání fotky.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const compressed = await compressImage(dataUrl);
      await uploadAvatarMutation.mutateAsync(compressed);
    } catch {
      setError('Nahrání selhalo, zkuste to znovu.');
    } finally {
      setUploading(false);
    }
  };

  const initials = name?.charAt(0).toUpperCase() ?? '?';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Avatar
          src={currentUrl ?? undefined}
          sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: '#D02964' }}
        >
          {initials}
        </Avatar>
        <Tooltip title="Nahrát fotku profilu">
          <IconButton
            size="small"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              bgcolor: '#D02964',
              color: '#fff',
              width: 28,
              height: 28,
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: '#b52358' },
            }}
          >
            {uploading ? (
              <CircularProgress size={14} sx={{ color: '#fff' }} />
            ) : (
              <PhotoCameraIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </Tooltip>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
        {uploading ? 'Ukládám…' : (error ?? 'Klikněte pro změnu fotky')}
      </Typography>
    </Box>
  );
};

export default AvatarUploadButton;
